/**
 * Angular Component. Checkbox with BMAT Style
 */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  Input,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';

export interface CheckboxChangeEvent {
  source: CheckboxComponent;
  checked: boolean;
}

@Component({
  selector: 'bm-checkbox',
  templateUrl: 'bm-checkbox.component.html',
  styleUrls: ['bm-checkbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // tslint:disable-next-line:no-forward-ref
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
  animations: [
    trigger('fadeInOut', [
      state('in', style({ opacity: 1 })),
      transition('void => *', [
        style({
          opacity: 0,
        }),
        animate('0.2s ease-in'),
      ]),
      transition('* => void', [
        animate(
          '0.2s ease-out',
          style({
            opacity: 0,
          }),
        ),
      ]),
    ]),
  ],
})
export class CheckboxComponent implements ControlValueAccessor {
  @ViewChild('input', { static: true }) public input!: ElementRef;
  @HostBinding('class.bm-checkbox--focused')
  public get isFocused() {
    return this._focused;
  }

  /**
   * Input values to directly copy to inner checkbox
   */
  @Input() public name = '';
  @Input() public tabIndex = 0;
  @Input() public required!: boolean;
  @Input() public disabled!: boolean;
  @Input() public readonly!: boolean;
  // tslint:disable-next-line:no-input-rename
  @Input('aria-label') public ariaLabel!: string;
  // tslint:disable-next-line:no-input-rename
  @Input('aria-labelledby') public ariaLabelledby!: string;

  @Input()
  public get checked() {
    return this.input.nativeElement.checked;
  }
  public set checked(checked: boolean) {
    this._renderer.setProperty(this.input.nativeElement, 'checked', checked);
  }

  private _focused!: boolean;
  private _onChangeCb!: (_: boolean) => void;
  private _onTouchedCb!: () => void;

  constructor(
    private _renderer: Renderer2,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {}

  // Focus Blur Handle
  public inputFocus() {
    this._focused = true;
  }

  public inputBlur() {
    this._focused = false;

    if (this._onTouchedCb) {
      this._onTouchedCb();
    }
  }

  public writeValue(value: boolean) {
    this.checked = !!value;
    this._changeDetectorRef.markForCheck();
  }

  // tslint:disable-next-line:no-any
  public registerOnChange(fn: (_: boolean) => void) {
    this._onChangeCb = fn;
  }

  public registerOnTouched(fn: () => void) {
    this._onTouchedCb = fn;
  }

  public inputChange(event: Event) {
    const { checked } = <HTMLInputElement>event.target;

    if (this._onChangeCb) {
      this._onChangeCb(checked);
    }
  }

  public focus() {
    if (this.input && this.input.nativeElement) {
      this.input.nativeElement.focus();
    }
  }
}
